// Session tracking utility for frontend
// Creates sessions, records page views with real time-on-page, and ends sessions reliably.

import { getParsedUser } from './authStorage';
import { getOrCreateVisitorId } from './referralTracking';
import {
  detectDeviceFromUserAgent,
  detectViewportLayout,
  normalizeBrowser,
  detectOS,
} from './userAgentParse';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/+$/, '') || '';

/** Start a new session after 30 minutes of inactivity (matches common analytics tools). */
const SESSION_IDLE_MS = 30 * 60 * 1000;
const SESSION_ID_KEY = 'raheasy_session_id';
const SESSION_SEEN_KEY = 'raheasy_session_last_seen';

const devLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(...args);
  }
};

// Get or create session ID; rotates after 30 min of inactivity so durations stay accurate
export const getSessionId = () => {
  const now = Date.now();
  let sessionId = null;
  let lastSeen = 0;
  try {
    sessionId = localStorage.getItem(SESSION_ID_KEY);
    lastSeen = Number(localStorage.getItem(SESSION_SEEN_KEY) || 0);
  } catch {
    /* storage unavailable */
  }
  if (!sessionId || (lastSeen && now - lastSeen > SESSION_IDLE_MS)) {
    sessionId = `session_${now}_${Math.random().toString(36).slice(2, 11)}`;
    try {
      localStorage.setItem(SESSION_ID_KEY, sessionId);
    } catch {
      /* ignore */
    }
  }
  try {
    localStorage.setItem(SESSION_SEEN_KEY, String(now));
  } catch {
    /* ignore */
  }
  return sessionId;
};

// Get user ID if logged in
const getUserId = () => {
  const user = getParsedUser();
  return user ? user._id || user.id || null : null;
};

/**
 * POST JSON; keepalive lets the request survive page unload
 * (plain fetch during unload is cancelled by the browser).
 */
const postJson = (url, data, { keepalive = false } = {}) =>
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    keepalive,
  }).catch((error) => devLog('Session tracking error:', error));

let initializedSessionId = null;
let initPromise = Promise.resolve();

// Initialize session (idempotent per session id; re-inits automatically after idle rotation)
export const initSession = () => {
  try {
    if (!API_BASE_URL) {
      devLog('API_BASE_URL not set, session tracking disabled');
      return Promise.resolve();
    }

    const sessionId = getSessionId();
    if (initializedSessionId === sessionId) return initPromise;
    initializedSessionId = sessionId;

    const visitorId = getOrCreateVisitorId() || undefined;
    initPromise = postJson(`${API_BASE_URL}/api/sessions`, {
      sessionId,
      visitorId,
      userId: getUserId() || undefined,
      device: detectDeviceFromUserAgent(navigator.userAgent),
      viewportLayout: detectViewportLayout(window.innerWidth),
      browser: normalizeBrowser(navigator.userAgent),
      os: detectOS(navigator.userAgent, navigator.platform),
      userAgent: navigator.userAgent,
      startedAt: new Date().toISOString(),
      isActive: true,
    });
    return initPromise;
  } catch (error) {
    devLog('Session tracking error:', error);
    return Promise.resolve();
  }
};

// —— Time-on-page tracking ——
// Counts only time the tab is visible; flushed on route change, tab hide, and page unload.

let currentPath = null;
let visibleMs = 0;
let visibleStartMs = 0;

const accumulateVisibleTime = () => {
  if (visibleStartMs > 0) {
    visibleMs += Date.now() - visibleStartMs;
    visibleStartMs = 0;
  }
};

/** Update timeSpent for the current page (timeOnly: no pageViews increment). */
const flushTimeSpent = ({ keepalive = false } = {}) => {
  if (!API_BASE_URL || !currentPath) return;
  const seconds = Math.round(visibleMs / 1000);
  if (seconds <= 0) return;
  const sessionId = getSessionId();
  postJson(
    `${API_BASE_URL}/api/sessions/${sessionId}/pageview`,
    {
      path: currentPath,
      timeSpent: seconds,
      timeOnly: true,
      timestamp: new Date().toISOString(),
    },
    { keepalive }
  );
};

/**
 * Record a route change: flushes time spent on the previous page,
 * then registers the new page view (counted once per page).
 */
export const trackPageEnter = (pathname) => {
  try {
    if (!API_BASE_URL) return;

    if (currentPath && currentPath !== pathname) {
      accumulateVisibleTime();
      flushTimeSpent();
    }

    currentPath = pathname;
    visibleMs = 0;
    visibleStartMs =
      typeof document !== 'undefined' && document.visibilityState === 'visible'
        ? Date.now()
        : 0;

    const sessionId = getSessionId();
    const send = () =>
      postJson(`${API_BASE_URL}/api/sessions/${sessionId}/pageview`, {
        path: pathname,
        timeSpent: 0,
        timestamp: new Date().toISOString(),
      });
    // Ensure the session exists first (avoids 404 after idle rotation)
    Promise.resolve(initSession()).then(send, send);
  } catch (error) {
    devLog('Session page view update error:', error);
  }
};

// End session
export const endSession = ({ keepalive = true } = {}) => {
  try {
    if (!API_BASE_URL) return;
    const sessionId = getSessionId();
    postJson(`${API_BASE_URL}/api/sessions/${sessionId}/end`, {}, { keepalive });
  } catch (error) {
    devLog('Session end error:', error);
  }
};

/**
 * Wire up visibility/unload handlers so time-on-page and session end
 * are reported even when the tab is hidden or closed. Returns cleanup.
 */
export const setupSessionLifecycle = () => {
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      accumulateVisibleTime();
      flushTimeSpent({ keepalive: true });
    } else {
      visibleStartMs = Date.now();
      // Returning after idle starts a fresh session
      initSession();
    }
  };

  const onPageHide = () => {
    accumulateVisibleTime();
    flushTimeSpent({ keepalive: true });
    endSession({ keepalive: true });
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
  };
};

/** @deprecated use trackPageEnter — kept for backwards compatibility. */
export const updateSessionPageView = (pathname) => trackPageEnter(pathname);
